import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useDefaultServiceMode, useSelectRestaurant } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useStoreCardButtonOptions } from 'hooks/use-store-card-button-options';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { allowAll } from 'utils';
import { EventName, emitEvent } from 'utils/event-hub';
import { AttributeName } from 'utils/performance';
import { useIsMobileOrderingAvailable } from 'utils/restaurant';
import { routes } from 'utils/routing';

import { ACTION_BUTTON_ICONS } from '../constants/action-button-icons';
import { IActionOption } from '../types';

interface IUseStoreActionOptions {
  (options: { restaurant: IRestaurantNode }): IActionOption[];
}

const omitOffersOption = (option: StoreCardButtonOptions) =>
  option !== StoreCardButtonOptions.OFFERS;

export const useStoreActionOptions: IUseStoreActionOptions = ({ restaurant }) => {
  const { selectRestaurant } = useSelectRestaurant({
    restaurant,
  });
  const { selectedStaticMenuItemId } = useMenuContext();
  const { fetchingPosData } = useOrderContext();
  const storeCardButtonOptions = useStoreCardButtonOptions();
  const { trackEvent } = useCdpContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const storeSelection2_0Enabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0);
  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);

  const { restaurantCanBeSelected, availablePickupServiceModes } = useServiceModeStatus(restaurant);

  const { setServiceMode } = useServiceModeContext();
  const defaultServiceMode = useDefaultServiceMode({ restaurant });
  const [selectedButtonOption, setSelectedButtonOption] = useState(StoreCardButtonOptions.INFO);
  const isRestaurantOpen = Boolean(availablePickupServiceModes.length);

  const logClick = useCallback(
    (name: StoreCardButtonOptions) => {
      trackEvent({
        name: CustomEventNames.BUTTON_CLICK_STORE_BUTTON,
        type: EventTypes.Other,
        attributes: {
          Name: name,
          StoreID: restaurant.number,
        },
      });
    },
    [trackEvent, restaurant]
  );

  /**
   * DEFINE BUTTON CLICK HANDLERS
   */
  const onInfoClick = useCallback(() => {
    logClick(StoreCardButtonOptions.INFO);
    setSelectedButtonOption(StoreCardButtonOptions.INFO);
    navigate(`${routes.store}/${restaurant._id}`);
  }, [logClick, navigate, restaurant._id]);

  const onOffersClick: VoidFunction = useCallback(() => {
    logClick(StoreCardButtonOptions.OFFERS);
    setSelectedButtonOption(StoreCardButtonOptions.OFFERS);

    selectRestaurant({
      callback: () => {
        setServiceMode(defaultServiceMode);
        navigate(routes.offers);
      },
    });
  }, [defaultServiceMode, logClick, navigate, selectRestaurant, setServiceMode]);

  const onOrderClick = useCallback(() => {
    logClick(StoreCardButtonOptions.ORDER);
    setSelectedButtonOption(StoreCardButtonOptions.ORDER);

    emitEvent(EventName.RESTAURANT_SELECTED_ON_ORDER, {
      selectedStaticMenuItemId,
      attributes: [
        {
          name: AttributeName.RESTAURANT_ID,
          value: restaurant.id || '',
        },
      ],
    });

    selectRestaurant();
  }, [logClick, restaurant, selectRestaurant, selectedStaticMenuItemId]);

  /**
   * MAP BUTTON OPTIONS TO CLICK HANDLER
   */
  const actionButtonClickHandlers = useMemo(
    () => ({
      [StoreCardButtonOptions.INFO]: onInfoClick,
      [StoreCardButtonOptions.OFFERS]: onOffersClick,
      [StoreCardButtonOptions.ORDER]: onOrderClick,
    }),
    [onInfoClick, onOffersClick, onOrderClick]
  );

  /**
   * MAP BUTTON OPTIONS TO TEXT
   */
  const actionButtonText = useMemo(() => {
    const shouldUseMenuText =
      storeSelection2_0Enabled &&
      restaurant.hasMobileOrdering &&
      isRestaurantOpen &&
      !isMobileOrderingAvailable;

    return {
      [StoreCardButtonOptions.INFO]: formatMessage({ id: 'info' }),
      [StoreCardButtonOptions.OFFERS]: formatMessage({ id: 'offers' }),
      [StoreCardButtonOptions.ORDER]: shouldUseMenuText
        ? formatMessage({ id: 'menu' })
        : formatMessage({ id: 'order' }),
    };
  }, [
    formatMessage,
    isMobileOrderingAvailable,
    isRestaurantOpen,
    restaurant.hasMobileOrdering,
    storeSelection2_0Enabled,
  ]);

  /**
   * FILTER STORE LOCATOR OFFERS BUTTONS BASED ON SANITY PROPERTY
   */
  // the initial value for showStoreLocatorOffersButton is not being properly set in Sanity and it defaults to NULL instead of true
  // in order to implement the requirements for this ticket, we hide the offers button only if the flag is explicitly set to FALSE
  const filteredStoreLocatorButtons = storeCardButtonOptions.filter(
    restaurant?.showStoreLocatorOffersButton === false ? omitOffersOption : allowAll
  );

  /**
   * CREATE STORE ACTION OPTIONS
   */
  return useMemo(() => {
    const getDisabled = (option: StoreCardButtonOptions) => {
      switch (option) {
        case StoreCardButtonOptions.ORDER:
        case StoreCardButtonOptions.OFFERS:
          return !restaurantCanBeSelected;

        default:
          return false;
      }
    };

    const getLoading = (option: StoreCardButtonOptions) => {
      return fetchingPosData && selectedButtonOption === option;
    };

    // console.log('the storeCardButtonOptions ->', storeCardButtonOptions);

    return filteredStoreLocatorButtons.map(option => {
      return {
        id: option,
        text: actionButtonText[option],
        onClick: actionButtonClickHandlers[option],
        icon: ACTION_BUTTON_ICONS[option],
        disabled: getDisabled(option),
        loading: getLoading(option),
      };
    });
  }, [
    actionButtonClickHandlers,
    actionButtonText,
    fetchingPosData,
    restaurantCanBeSelected,
    selectedButtonOption,
    filteredStoreLocatorButtons,
  ]);
};
